// Read-only collapsible taxonomy tree view.
// Used on the Active tab to display the current taxonomy.
// Tier 0 (roots) and tier 1 (their children) are visible by default; deeper nodes are collapsed.

import { useState, useCallback } from 'react'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { TreeNode } from './types'

interface TaxonomyTreeProps {
  roots: TreeNode[]
}

export function TaxonomyTree({ roots }: TaxonomyTreeProps) {
  // Track expanded node IDs. Default: all depth-0 nodes are expanded (showing their depth-1 children).
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    for (const root of roots) {
      if (root.children.length > 0) {
        initial.add(root.id)
      }
    }
    return initial
  })

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  return (
    <div className="space-y-0.5">
      {roots.map((root) => (
        <TreeNodeRow key={root.id} node={root} expanded={expanded} onToggle={toggle} />
      ))}
    </div>
  )
}

interface TreeNodeRowProps {
  node: TreeNode
  expanded: Set<string>
  onToggle: (id: string) => void
}

function TreeNodeRow({ node, expanded, onToggle }: TreeNodeRowProps) {
  const hasChildren = node.children.length > 0
  const isExpanded = expanded.has(node.id)
  const indent = node.depth * 24

  return (
    <div>
      <div
        className={`group flex items-center gap-1.5 rounded-sm py-1 pr-2 hover:bg-muted/50 ${
          node.depth === 0 ? 'font-semibold' : ''
        } ${node.depth === 1 ? 'text-sm font-medium' : ''} ${
          node.depth >= 2 ? 'text-sm text-muted-foreground' : ''
        }`}
        style={{ paddingLeft: `${indent + 4}px` }}
      >
        {/* Expand/collapse chevron */}
        {hasChildren ? (
          <button
            onClick={() => onToggle(node.id)}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm hover:bg-muted"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="w-5 shrink-0" />
        )}

        {/* Path code */}
        <span className="w-10 shrink-0 text-xs tabular-nums text-muted-foreground/60">
          {node.path.split('.').pop()}
        </span>

        {/* Name with optional description tooltip */}
        {node.description ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-default">{node.name}</span>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              <p className="text-sm">{node.description}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <span>{node.name}</span>
        )}
      </div>

      {/* Children (only rendered when expanded) */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TreeNodeRow key={child.id} node={child} expanded={expanded} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  )
}
